'use client';
import { sendGAEvent } from '@next/third-parties/google';
import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { BaseFillGift } from '@/app/components/icons/BaseFillGift';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import { InfoIcon } from '@/app/components/icons/InfoIcon';
import { Loading } from '@/app/components/icons/Loading';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { trackPayButtonClick } from '@/app/components/shared/RedditPixel';
import { useRetentionStrategy } from '@/app/components/shared/RetentionStrategy/hooks/use-retention-strategy';
import { trackInitiateCheckout } from '@/app/components/shared/TikTokPixel';
import { Button } from '@/app/components/ui/button';
import { SITE } from '@/app/constants';
import { useGlobalModal, usePayment, usePaymentIntent } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { SubscriptionProductType } from '@/app/types';
import { getSubscriptionUrl, reportEvent } from '@/app/utils';
import { openOrRedirect } from '@/app/utils/client';
import { usePathname } from '@/i18n/routing';
import { StripeConfirmDialog } from '../../Pay/components/StripeConfirmDialog';
import type { CreditV1PeriodProduct } from '../types';
import { CancelNextPlan } from './CancelNextPlan';
import { Unsubscribe } from './Unsubscribe';

const BUTTON_STYLE_MAP: Record<
  'standard_v2' | 'premium_v2' | 'ultimate_v2',
  { style: React.CSSProperties; textClass: string }
> = {
  standard_v2: {
    style: {
      background: 'linear-gradient(90deg, #DFEFFF 0%, #B4CCFF 100%)',
      boxShadow: '0px 10px 40px 0px rgba(60,114,152,0.4)',
    },
    textClass: 'text-black',
  },
  premium_v2: {
    style: {
      background: 'linear-gradient(90deg, #FFFBE9 0%, #E6D1AB 100%)',
      boxShadow: '0px 10px 40px 0px rgba(152,103,60,0.4)',
    },
    textClass: 'text-black',
  },
  ultimate_v2: {
    style: {
      backgroundImage:
        SITE === 'china'
          ? 'url(https://image01.vidu.zone/vidu/media-asset/ultimate_v2-2429cee5.webp)'
          : 'url(https://image01.cf.vidu.studio/vidu/media-asset/ultimate_v2-2429cee5.webp)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      boxShadow: 'inset 0px 4px 10px 0px rgba(255,255,255,0.25), 0px 10px 40px 0px rgba(87,101,242,0.4)',
    },
    textClass: 'text-white',
  },
};

const AvailableButton = ({
  product,
  loading,
  onClick,
  bonusCode,
}: {
  product: SubscriptionProductType;
  loading: boolean;
  onClick: () => void;
  bonusCode: string | null;
}) => {
  const t = useTranslations();
  const conf = BUTTON_STYLE_MAP[product.plan_type as keyof typeof BUTTON_STYLE_MAP];

  return product.product_pay_status === 'downgrade' || product.product_pay_status === 'change' ? (
    <Button
      data-agl-cvt="7"
      variant="outline"
      className="group relative h-12 w-full rounded-12 text-sm"
      disabled={loading}
      onClick={onClick}
    >
      {loading && <Loading className="animate-spin text-base" />}
      {product.product_pay_status === 'downgrade'
        ? t('payments.plans.downgradeButton')
        : t('payments.plans.changeButton')}
    </Button>
  ) : (
    <Button
      className={`group relative h-12 w-full rounded-12 text-sm ${conf?.textClass ?? 'text-black'}`}
      style={conf?.style}
      data-agl-cvt="7"
      disabled={loading}
      onClick={onClick}
    >
      {loading && <Loading className="animate-spin text-base" />}
      {bonusCode && (product.extra_grant_credit || 0) > 0 ? (
        <div className="-top-3 -right-2 absolute flex h-6 items-center gap-2 rounded-8 rounded-bl-none bg-[linear-gradient(90deg,#D8C8FF_0%,#60CFFF_100%)] px-2 text-xs">
          <BaseFillGift className="size-3" /> 已使用达人合作码
        </div>
      ) : null}
      <span className="relative z-10">
        {product.product_pay_status === 'upgrade'
          ? t('payments.plans.upgradeButton')
          : product.product_type === 'pass'
            ? t('payments.plans.passButton')
            : t('payments.plans.subscribeButton')}
      </span>
    </Button>
  );
};

const CurrentButton = ({ loading, onClick, pathname }: { loading: boolean; onClick: () => void; pathname: string }) => {
  const t = useTranslations();
  return (
    <Button
      className="h-12 w-full rounded-12 bg-system-white12 font-normal text-sm hover:bg-system-white24"
      disabled={loading}
      onClick={onClick}
    >
      {t('payments.plans.currentText')}
      {!pathname.startsWith('/subscription/') && <BaseLineRight className="ml-2 h-5 w-5 shrink-0" />}
    </Button>
  );
};

const ResubscribeButton = ({ loading, onClick }: { loading: boolean; onClick: () => void }) => {
  return (
    <Button
      className="relative h-12 w-full overflow-hidden rounded-12 border border-white bg-gradient-to-r from-[#b2d7ff] to-[#ffe3da] font-semibold text-black text-sm shadow-[inset_0px_0px_10px_0px_white] hover:opacity-90"
      disabled={loading}
      onClick={onClick}
    >
      {loading && <Loading className="animate-spin text-base" />}
      继续订阅
    </Button>
  );
};

type SubscriptionButtonProps = {
  loading: boolean;
  product: SubscriptionProductType;
  getManagement: () => void;
  source?: string;
  /** 弹窗来源：打开套餐弹窗时的 from_page，仅弹窗场景（source==='dialog'）随 vip_price_click 上报为 modal_from_page */
  modalFromPage?: string;
};

export const SubscriptionButton = ({
  loading,
  product,
  getManagement,
  source = '',
  modalFromPage,
}: SubscriptionButtonProps) => {
  const { subscription, userInfo, nextPlan, payStatus, bonusCode, clawPass, isTeamSpace } = useStore((state) => ({
    payStatus: state.payStatus,
    subscription: state.subscription,
    userInfo: state.userInfo,
    nextPlan: state.nextPlan,
    bonusCode: state.bonusCode,
    clawPass: state.clawPass,
    isTeamSpace: state.isTeamSpace,
  }));
  const { pay } = usePayment();
  const t = useTranslations();
  const { savePaymentIntent } = usePaymentIntent();
  const pathname = usePathname();
  const lastProductRef = useRef<SubscriptionProductType | null>(null);
  const { isEligible: isRetentionEligible } = useRetentionStrategy();
  const [showStripeConfirm, setShowStripeConfirm] = useState(false);

  const { open: openLoginDialog } = useGlobalModal('loginDialog');

  // vip_price_click 参数：from_page 标识点击发生在订阅页面还是套餐弹窗；
  // 弹窗场景额外带 modal_from_page（打开弹窗时的 from_page），页面场景不带。
  const buildVipPriceClickParams = () => {
    const isDialog = source === 'dialog';
    return {
      from_page: isDialog ? 'vip_price_modal_exposure' : 'vip_price_exposure',
      subscribe_type: `${product.product_type}-${product.billing_period}`,
      vip_type: product.plan_type,
      ...(isDialog ? { modal_from_page: modalFromPage } : {}),
    };
  };

  const handleClickPayment = () => {
    // 记录当前产品信息，用于在支付成功时判断是否为降级操作
    lastProductRef.current = product;

    sendGAEvent('event', 'subscription_button_click', {
      subscribe_type: `${product.product_type}-${product.billing_period}`,
      vip_type: product.plan_type,
      page_type: source === 'dialog' ? 'dialog_box' : 'page',
    });

    trackInitiateCheckout();
    trackPayButtonClick();

    // 检查用户是否已登录
    if (!userInfo) {
      // 保存购买意图到localStorage
      const intent = {
        productId: product.id,
        timestamp: Date.now(),
      };
      savePaymentIntent(intent);

      // 弹出登录弹窗
      openLoginDialog();
      reportEvent('login_pop_exposure', {
        refer_element: 'vip-price-modal',
        refer_page: pathname,
      });
      return;
    }

    if (isRetentionEligible && product.product_pay_status === 'upgrade') {
      reportEvent('subscription_reactivation_click', { pricing: 'upgrade' });
    }

    if (product.product_pay_status === 'available') {
      // stripe 有可用优惠券时先弹选券确认框，不提前调用 pay()
      if (product.pay_channel === 'stripe' && (product as CreditV1PeriodProduct).available_coupons?.length) {
        setShowStripeConfirm(true);
        return;
      }
      pay(product);
      // 立即订阅
      reportEvent('vip_price_click', buildVipPriceClickParams());
    } else {
      // 升级 / 变更：直接进入变更弹窗，券在变更弹窗内选；这里不再弹 StripeConfirmDialog
      pay(product);
      // 变更订阅
      reportEvent('vip_change_exposure', {
        change_type: product.product_pay_status,
        subscribe_type: `${product.product_type}-${product.billing_period}`,
        vip_type: product.plan_type,
        subscribe_type_origin: `${subscription?.product_type}-${subscription?.billing_period}`,
        vip_type_original: subscription?.plan_type || '',
      });
    }
  };

  // 查看当前预定套餐
  const handleClickCurrentPlan = () => {
    if (SITE === 'global' && product.plan_type !== 'free') {
      getManagement();
    } else {
      // 如果当前页面不是my-subscription页面，则跳转到my-subscription页面
      if (!pathname.startsWith('/subscription/')) {
        reportEvent('page_view', {
          page_name: 'my-subscription',
          from_page: pathname === '/pricing' ? 'pricing' : 'vip_price_modal',
        });
        // 延迟100ms，确保事件上报成功
        setTimeout(() => {
          openOrRedirect(getSubscriptionUrl(isTeamSpace));
        }, 100);
      }
    }
  };

  // 不可预定产品hover气泡提示文案
  const getTooltipContent = () => {
    // 如果当前是claw pass套餐
    if (
      clawPass?.has_benefit === true &&
      clawPass?.type === 'claw_pass' &&
      clawPass?.product_type === 'claw_pass_bundle'
    ) {
      // clawPass 激活中不可购买提示
      return t('payments.plans.clawPassActiveUnavailable');
    }

    // 有下一个套餐
    if (nextPlan?.next_plan_type) {
      return t('payments.plans.nextPlanSupport');
    }
    // 非免费套餐, 支付宝渠道
    if (product.pay_channel === 'alipay') {
      // 当前是订阅套餐，无法按月/按年购买
      if (subscription?.product_type === 'subscription' && product?.product_type === 'pass') {
        return `您已订阅，无法按${product.billing_period === 'monthly' ? '月' : '年'}购买`;
      }
      // 当前订阅的是免费套餐，无法按月/按年购买
      if (subscription?.product_type === 'pass' && product?.product_type === 'subscription') {
        return `您已按${subscription.billing_period === 'monthly' ? '月' : '年'}购买，无法订阅`;
      }
      return '不支持降级';
    }
    return t('payments.plans.notSupport');
  };

  const renderButton = () => {
    // 如果产品有sub_change_id，则显示取消变更按钮
    if (product.sub_change_id && product.sub_change_id !== '0') {
      return (
        <CancelNextPlan product={product}>
          <Button
            className="relative h-12 w-full rounded-12"
            variant="outline"
            loading={loading || payStatus === 'loading'}
          >
            {t('payments.plans.cancelChangeButton')}
            <div className="absolute top-0 right-0 rounded-tr-8 rounded-bl-8 bg-[rgba(105,118,255,0.40)] px-[6px] font-normal text-[#A9B0FF] text-xs leading-[18px]">
              {t('payments.plans.nextPlan')}
            </div>
          </Button>
        </CancelNextPlan>
      );
    }
    // 继续订阅（取消自动续费的国内用户）
    if (product.product_pay_status === 'continue') {
      return (
        <ResubscribeButton
          loading={loading || payStatus === 'loading'}
          onClick={() => {
            if (isRetentionEligible) {
              reportEvent('subscription_reactivation_click', { pricing: 'renew' });
            }
            pay(product);
          }}
        />
      );
    }
    // 当前套餐
    if (product.product_pay_status === 'current') {
      return <CurrentButton loading={loading} onClick={handleClickCurrentPlan} pathname={pathname} />;
    }
    // 如果产品可订阅，则显示可订阅按钮
    if (['available', 'change', 'downgrade', 'upgrade'].includes(product.product_pay_status)) {
      return (
        <AvailableButton
          product={product}
          loading={loading || payStatus === 'loading'}
          onClick={handleClickPayment}
          bonusCode={bonusCode}
        />
      );
    }

    if (product.plan_type === 'free' && subscription && subscription.is_unsubscribe) {
      return (
        <Unsubscribe subscription={subscription}>
          <Button data-agl-cvt="7" variant="outline" className="group relative h-12 w-full rounded-12 text-sm">
            {t.rich('payments.plans.cancelPlan', {
              span: (_chunks) => <span className="text-system-white64">（Cancel）</span>,
            })}
          </Button>
        </Unsubscribe>
      );
    }

    // 不可预定 & 不是当前预定套餐
    return (
      <CustomTooltip side="bottom" content={getTooltipContent()} trigger="auto" disableHoverableContent={true}>
        <Button className="h-12 w-full rounded-12 bg-system-white12 text-sm text-system-white36">
          {product.plan_type === 'free' ? t('payments.plans.freeButton') : t('payments.plans.subscribeButton')}
          <InfoIcon className="ml-2 h-4 w-4 shrink-0" />
        </Button>
      </CustomTooltip>
    );
  };

  return (
    <>
      {renderButton()}
      {/* 只在需要时挂载，避免每张 PlanCard 都常驻一个 dialog 触发接口 */}
      {showStripeConfirm && (
        <StripeConfirmDialog
          product={product}
          open={showStripeConfirm}
          onClose={() => setShowStripeConfirm(false)}
          onConfirm={(couponId) => {
            setShowStripeConfirm(false);
            pay(product, { couponIdOverride: couponId });
            reportEvent('vip_price_click', buildVipPriceClickParams());
          }}
        />
      )}
    </>
  );
};
